import { useState, useEffect } from 'react';

export default function ReadingProgress() {
  const [progress, setProgress] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    /* Tiny entrance delay so the bar "slides in" on load */
    const t = setTimeout(() => setMounted(true), 300);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const total = scrollHeight - clientHeight;
      setProgress(total > 0 ? (scrollTop / total) * 100 : 0);
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
    return () => window.removeEventListener('scroll', update);
  }, []);

  return (
    <>
      {/* ── Reading progress bar ── */}
      <div
        className={`fixed top-0 left-0 w-full z-[70] pointer-events-none transition-all duration-500 ${mounted ? 'opacity-100' : 'opacity-0'}`}
      >
        {/* Track */}
        <div className="h-[3px] w-full bg-gray-200/60"></div>
        {/* Fill */}
        <div
          className="absolute top-0 left-0 h-[3px] transition-[width] duration-75 ease-linear rounded-r-full"
          style={{
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #18c7f4 0%, #384774 60%, #18c7f4 100%)',
            backgroundSize: '200% 100%',
            animation: progress > 0 ? 'progressShimmer 2s linear infinite' : 'none',
          }}
        />
        {/* Glow dot at tip */}
        {progress > 1 && progress < 99.5 && (
          <div
            className="absolute top-0 -translate-y-[1px] w-3 h-3 rounded-full pointer-events-none"
            style={{
              left: `calc(${progress}% - 6px)`,
              background: 'radial-gradient(circle, #18c7f4 0%, transparent 70%)',
              opacity: 0.9,
              transition: 'left 75ms linear',
            }}
          />
        )}
      </div>

      {/* ── Percentage badge (top-right) ── */}
      <div
        className={`fixed top-[18px] right-5 z-[71] pointer-events-none transition-all duration-500 ${
          mounted && progress > 3 ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
        }`}
      >
        <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full px-2.5 py-1 shadow-sm">
          <div
            className="w-5 h-5 relative flex items-center justify-center"
            style={{ '--p': `${Math.round(progress)}` } as React.CSSProperties}
          >
            {/* mini circular progress */}
            <svg viewBox="0 0 20 20" className="w-5 h-5 -rotate-90">
              <circle cx="10" cy="10" r="8" fill="none" stroke="#e5e7eb" strokeWidth="2.5" />
              <circle
                cx="10"
                cy="10"
                r="8"
                fill="none"
                stroke="#18c7f4"
                strokeWidth="2.5"
                strokeDasharray={`${2 * Math.PI * 8}`}
                strokeDashoffset={`${2 * Math.PI * 8 * (1 - progress / 100)}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 75ms linear' }}
              />
            </svg>
          </div>
          <span className="font-body text-[10px] font-bold text-secondary tabular-nums">
            {Math.round(progress)}%
          </span>
        </div>
      </div>

      <style>{`
        @keyframes progressShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </>
  );
}